/* eslint-disable no-console */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {TF} from '../../constants/Common';
import InformativeText from '../../constants/InformativeText';
import {ISelect} from '../../interface/ISelect';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import FafAPI from '../../services/modules/PRODUCTS/FafAPI';
import productInfoAPI from '../../services/modules/PRODUCTS/ProductInfoAPI';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {shouldBlockReadOnly} from '../../utils/SubscriptionHelper';
import DisplayModal from '../DisplayModal/DisplayModal';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {yearKeyLabel} from '../Forms/Constants/IP/commonKeyLabel';
import {B2bKey} from '../Forms/Constants/Products/B2BKeyLabel';
import {FafKey, FafLabel} from '../Forms/Constants/Products/FafKeyLabel';
import {
  RawMaterialKey,
  RawMaterialLabel
} from '../Forms/Constants/Products/RawMaterialKeyAndLabel';
import FafForm from '../Forms/FafForm';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import Loader from '../Loader/Loader';

import {FullInfo, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {returnProductFilter} from './Utils/returnProductFilters';
import sumValue from './Utils/sumValue';
import {
  excludeAcceptedIdsForDelete,
  getBulkSelectedIdsInDataSource,
  useBulkCheckerModals
,
  createBulkRowSelection
} from './Utils/TableBulkSelectionBar';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const fafTable: React.FC<{
  selectedYear: string;
}> = ({selectedYear}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    fuelType: string;
    noOfUnits: number;
    deviceType: number;
    quantity: number;
    cost: number;
    driven: number;
    status?: string;
  }

  const companyId = useCompanyStore.getState().companyId;
  const [rawMaterialFilter, setRawMaterialFilter] = useState<any>({});
  const [formRawMaterialFilter, setFormRawMaterialFilter] = useState<any>({});
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [formSelectedYear, setFormSelectedYear] = useState('');

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const [showModel, setShowModel] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const bulkSelectedIds = useMemo(
    () => getBulkSelectedIdsInDataSource(selectedRowKeys, dataSource),
    [selectedRowKeys, dataSource]
  );
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const products = useJSONStore.getState().products;
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });

  const [allJSON, setAllJSON] = useState(false);
  const {productFilter} = rawMaterialFilter;

  useEffect(() => {
    if (selectedYear && allJSON) {
      setIsRefresh(true);
      Promise.all([
        FafAPI(APITypeEnum.GET_ALL, undefined, `${companyId}/${selectedYear}`),
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${selectedYear}`
        )
      ])
        .then(([res1, res2]) => {
          setDataSource(res1 ?? []);
          setRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: RawMaterialKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                }
              ],
              res2,
              {filterKey: B2bKey.PRODUCT_TYPE, filterValue: 'b2c'}
            )
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [selectedYear, allJSON]);

  useEffect(() => {
    if (formSelectedYear) {
      Promise.all([
        productInfoAPI(
          APITypeEnum.GET_ALL,
          undefined,
          `${companyId}/${formSelectedYear}`
        )
      ])
        .then(([res1]) => {
          setFormRawMaterialFilter(
            returnProductFilter(
              [
                {
                  mainKey: RawMaterialKey.PRODUCT_ID,
                  filterer: products,
                  subKey: '',
                  returnFilterKey: 'productFilter'
                }
              ],
              res1,
              {filterKey: B2bKey.PRODUCT_TYPE, filterValue: 'b2c'}
            )
          );
        })
        .catch(() => {})
        .finally(() => {
          setIsFormLoading(false);
        });
    }
  }, [formSelectedYear]);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      FafAPI(APITypeEnum.GET_ALL, undefined, `${companyId}/${selectedYear}`)
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  useEffect(() => {
    setIsRefresh(true);
    Promise.all([])
      .catch(() => {})
      .finally(() => {
        setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  const handleDelete = (_id: string[] | string) => {
    const ids = Array.isArray(_id) ? _id : [_id];
    const feedstocks = excludeAcceptedIdsForDelete(ids, dataSource);
    if (feedstocks.length === 0) {
      return;
    }
    FafAPI(APITypeEnum.DELETE_ALL, {
      feedstocks
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {})
      .finally(() => {});
  };

  const bulkChecker = useBulkCheckerModals({
    selectedIds: bulkSelectedIds,
    refresh,
    selectionContextKey: selectedYear,
    onSelectionContextChange: () => {
      setSelectedRowKeys([]);
    },
    getBulkDeleteEligibleCount: (ids) =>
      excludeAcceptedIdsForDelete(ids, dataSource).length,
    onSuccess: () => {
      setSelectedRowKeys([]);
      setRefresh(true);
    },
    onBulkStatusSubmit: async ({ids, status, comment}) =>
      await FafAPI(APITypeEnum.STATUS_ALL, {
        feedstocks: ids,
        status,
        [checkerStatusKey.COMMENT]: comment
      }),
    onBulkDelete: async (ids) => {
      const feedstocks = excludeAcceptedIdsForDelete(ids, dataSource);
      if (feedstocks.length === 0) {
        return false;
      }

      return await FafAPI(APITypeEnum.DELETE_ALL, {
        feedstocks
      });
    }
  });

  const handleSelectAll = () => {
    setSelectedRowKeys(dataSource.map((row) => row._id));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const fafKeyLabel = useMemo(() => {
    return [
      {
        key: yearKeyLabel.key,
        label: yearKeyLabel.label,
        type: objTypes.string
      },
      {
        key: FafKey.PRODUCT_ID,
        label: FafLabel.PRODUCT_ID,
        type: objTypes.json,
        format: formSelectedYear
          ? formRawMaterialFilter.productFilter
          : rawMaterialFilter.productFilter
      },
      {
        key: TF,
        label: FafLabel.FUEL_SOLD,
        type: objTypes.timeFrameData,
        renderType: 'multi'
      },
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              key: checkerStatusKey.STATUS,
              label: checkerStatusLabel.STATUS,
              type: objTypes.status
            },
            {
              key: checkerStatusKey.COMMENT,
              label: checkerStatusLabel.COMMENT,
              type: objTypes.comment
            }
          ]
        : [])
    ];
  }, [formRawMaterialFilter, rawMaterialFilter]);

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = [
    {
      title: FafLabel.PRODUCT_ID,
      dataIndex: FafKey.PRODUCT_ID,
      editable: true,
      type: 'select',
      ...SearchFilter(FafLabel.PRODUCT_ID, FafKey.PRODUCT_ID, productFilter),
      render: (value) => {
        return (
          <>
            {
              productFilter?.find((item: any) => {
                return item.value === value;
              })?.label
            }
          </>
        );
      }
    },
    {
      title: FafLabel.FUEL_SOLD,
      dataIndex: TF,
      editable: true,
      type: 'select',
      // render: (value) => {
      //   return (
      //     <TimeFrameRender value={value} dataKey={WasteKey.wasteProduced} />
      //   );
      // }
      render: (value) => {
        const mainData = value?.find((item: any) => {
          return item.type === FafKey.FUEL_SOLD;
        });

        return (
          <div
            onClick={() => {
              setOpenDisplayModal(true);
              setOpenDisplayModalData(mainData);
              setOpenDisplayModalType({
                title: `${FafLabel.FUEL_SOLD} (${mainData?.unit})`,
                dataKey: FafKey.FUEL_SOLD
              });
            }}
            style={{
              textDecorationLine: 'underline',
              pointerEvents: 'auto',
              cursor: 'pointer'
            }}
          >
            <div>{`${String(
              formatIndian(sumValue(mainData?.data))
            )} ${mainData?.unit}`}</div>
          </div>
        );
      }
    },
    ...(hasUserRole(UserRole.Maker_Role) ||
    hasUserRole(UserRole.Both_role) ||
    hasViewRole(UserRole.View_Only)
      ? [
          {
            title: RawMaterialLabel.STATUS_COMMENTS,
            dataIndex: 'status',
            editable: true,
            type: 'select',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <div
                  style={{
                    fontWeight: 700,
                    color: colorText.color,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}
                  {value === 'rejected' && record?.comment && (
                    <span className="customComment">- {record.comment}</span>
                  )}
                </div>
              );
            }
          }
        ]
      : []),
    // ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
    //   ? [
    //       {
    //         title: checkerStatusLabel.DATA_SOURCE,
    //         dataIndex: checkerStatusKey.DATA_SOURCE,
    //         editable: true,
    //         type: 'select',
    //         render: (value: any) => {
    //           return (
    //             <div>
    //               {value === checkerStatusKey.MANUAL
    //                 ? checkerStatusLabel.MANUAL
    //                 : checkerStatusLabel.BULK_UPLOAD}
    //             </div>
    //           );
    //         }
    //       }
    //     ]
    //   : []),
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            title: RawMaterialLabel.STATUS,
            dataIndex: 'status',
            render: (value: any, record: any) => {
              const colorText = getStatusColor(value);

              return (
                <Tag
                  onClick={() => {
                    if (shouldBlockReadOnly()) {
                      usePlatformStore
                        .getState()
                        .setOpenOnFreeTrialMessageModel(true);

                      return;
                    }
                    setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  color={colorText.color}
                  style={{
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {colorText.text}
                </Tag>
              );
            }
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            title: FullInfo.title,
            dataIndex: 'fullInfo',
            editable: true,
            type: 'select',
            render: (_: any, record: any) => {
              return (
                <Button
                  onClick={() => {
                    setShowModelInfo(true);
                    setEditData(record);
                    setIsEdit(true);
                  }}
                  shape="circle"
                  style={{
                    marginRight: 16,
                    cursor: 'pointer',
                    color: '#435B37',
                    background: '#F5FBF3',
                    border: 0
                  }}
                >
                  <ExclamationOutlined />
                </Button>
              );
            }
          }
        ]
      : []),
    ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
      ? [
          {
            title: Title.actionTitle,
            dataIndex: 'operation',
            type: '' as const,
            fixed: 'right' as const,
            width: '200px',
            render: (value: any, record: any) =>
              dataSource.length >= 1 ? (
                <ActionsButton
                  record={record}
                  setShowModel={setShowModel}
                  setEditData={setEditData}
                  setIsEdit={setIsEdit}
                  handleDelete={handleDelete}
                />
              ) : null
          }
        ]
      : [])
  ];

  return (
    <div>
      <TableTitleHeader
        title="Sold Products Info"
        filter={{facilityId: '', selectedYear}}
        addButton={{
          setShowModel,
          setIsEdit,
          buttonTitle: 'Add Sold Product'
        }}
        sampleDownload={{href: ''}}
        jobs={{setShowJobs: null, uploadType: ''}}
        deleteButton={{selectedRowKeys, handleDelete}}
        refresh={{setRefresh}}
        showAddOnly={true}
        onlyYear={true}
        tooltip={InformativeText.Product.faf}
        shouldEdit={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        }
        onSelectAll={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? handleSelectAll
            : undefined
        }
        allSelectableCount={dataSource.length}
        onDeselectAll={() => {
          setSelectedRowKeys([]);
        }}
        onChangeStatus={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? bulkChecker.openChangeStatus
            : undefined
        }
        onBulkDelete={
          hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
            ? bulkChecker.openBulkDelete
            : undefined
        }
      />
      {bulkChecker.bulkCheckerModals}
      <TableAnt
        // getPopupContainer={(triggerNode) =>
        //   triggerNode.closest(
        //     '.ant-modal, .ant-drawer, [style*="position"]'
        //   ) as HTMLElement
        // }
        rowSelection={createBulkRowSelection<DataType>({
          selectedRowKeys,
          setSelectedRowKeys,
          onSelectAll: handleSelectAll,
          onDeselectAll: () => {
            setSelectedRowKeys([]);
          },
          enabled:
            hasUserRole(UserRole.Maker_Role) ||
            hasUserRole(UserRole.Both_role)
        })}
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'auto'}}
        loading={{
          indicator: <Loader />,
          spinning: allJSON ? isRefresh : true
        }}
      />
      <DrawerForm
        open={showModel}
        onCancel={() => {
          setShowModel(false);
          setFormSelectedYear('');
        }}
        setRefresh={setRefresh}
        data={isEdit ? editData : undefined}
        FormComponent={
          <FafForm
            rawMaterialFilter={
              formSelectedYear ? formRawMaterialFilter : rawMaterialFilter
            }
            data={isEdit ? editData : undefined}
            setFormSelectedYear={setFormSelectedYear}
            setIsFormLoading={setIsFormLoading}
            isFormLoading={isFormLoading}
          />
        }
        API={FafAPI}
        idKey={`${companyId}/${selectedYear}`}
        table={true}
        onlyYear={true}
        stepsTitle={['Sold Product Details', 'Quantity Details']}
        formPreview={fafKeyLabel}
      />
      <DisplayModal
        title={openDisplayModalType.title}
        open={openDisplayModal}
        onCancel={() => {
          setOpenDisplayModal(false);
        }}
        displayComponent={
          <TimeFrameDataTooltip mainData={openDisplayModalData} />
        }
      />
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={fafKeyLabel} />
        }
        API={FafAPI}
        isChecker={true}
        isAction={false}
        stepsTitle={['All Data']}
      />
    </div>
  );
};

export default fafTable;
